import { EPhotoSize } from "../services/Photo";

/**
 * @namespace DataCache
 * @description Espacio de nombres que proporciona funciones para manejar el almacenamiento en caché de datos
 * utilizando la API de Cache Storage del navegador. Incluye métodos para agregar, recuperar y eliminar
 * recursos en caché, así como para gestionar versiones de caché y verificar soporte del navegador.
 */
export namespace DataCache {
    const HOST_NAME = location.hostname;
    const CACHE_VERSION = 2;
    const CACHE_NAME = `${HOST_NAME}-${CACHE_VERSION}`;
    const META_SUFFIX = "::meta";
    /**
     * Esta variable indica la variante para los elementos en caché,
     * utilizada para diferenciar versiones o modificaciones, por ejemplo, al cambiar una imagen.
     */
    const VARIANT_PARAM_FOR_CACHE_ITEM = "Modificacion";
    const IMAGE_SIZE_PARAM = "Tamanio";
    const IMAGE_SIZES_TO_ACCEPT = [EPhotoSize.THUMBNAIL];
    const DEBUG_PREFIX = HOST_NAME + "-cache-v" + CACHE_VERSION + " -> ";
    const EXPIRATION_TIME = 31000 * 60 * 1000; // 31,000 minutos = ~21.5 días

    /**
     * @function _DeleteCacheStorage
     * @description Elimina el almacenamiento en caché actual y limpia las cachés antiguas.
     * @returns {Promise<boolean>} Promesa que resuelve en `true` si la caché actual fue eliminada exitosamente.
     */
    export async function _DeleteCacheStorage() {
        await DeleteOldCaches();
        return caches.delete(CACHE_NAME);
    }

    /**
     * @function _DeleteItemResource
     * @description Elimina un recurso específico de la caché.
     * @param {string} url - La URL del recurso que se desea eliminar de la caché.
     * @returns {Promise<boolean>} Promesa que resuelve en `true` si el recurso fue eliminado exitosamente,
     * o `false` si no se encontró en la caché.
     */
    export async function _DeleteItemResource(url: string) {
        const cacheStorage = await caches.open(CACHE_NAME);
        const cachedResponse = await cacheStorage.match(url);

        if (!cachedResponse) {
            return false;
        }

        const deleted = await cacheStorage.delete(url);
        await cacheStorage.delete(url + META_SUFFIX);
        console.debug(DEBUG_PREFIX, "Deleted resourse", deleted, url);
        return deleted;
    }

    /**
     * @function _GetData
     * @description Intenta obtener datos de la caché. Si no están disponibles, los recupera de forma remota,
     * los guarda en la caché y luego los devuelve.
     * @param {string} url - La URL del recurso que se desea obtener.
     * @returns {Promise<Response>} Promesa que resuelve en la respuesta del recurso, ya sea desde la caché o desde la red.
     */
    export async function _GetData(url: string) {
        let cachedData = await GetCachedData(url);

        if (cachedData) {
            // console.log(debugPrefix, 'Retrieved cached data');
            return cachedData;
        }

        const response = await FetchAndSaveData(url);
        await DeleteOldCaches();

        return response;
    }

    /**
     * @function _IsSupported
     * @description Verifica si el navegador soporta el uso de la API de Cache Storage.
     * @returns {boolean} `true` si el navegador soporta Cache Storage, `false` en caso contrario.
     */
    export function _IsSupported(): boolean {
        return isSupported();
    }

    /**
     * @function isSupported
     * @description Función interna que verifica el soporte de Cache Storage en el navegador.
     * @returns {boolean} `true` si el navegador soporta Cache Storage, `false` en caso contrario.
     * @private
     */
    function isSupported(): boolean {
        return 'caches' in window && typeof caches.open === 'function';
    }

    /**
     * @function FetchAndSaveData
     * @description Recupera datos de forma remota y los guarda en la caché. Si la URL contiene ciertos
     * parámetros, maneja la lógica de variantes y tamaños de imagen.
     * @param {string} url - La URL del recurso que se desea recuperar y guardar.
     * @returns {Promise<boolean>} Promesa que resuelve en la respuesta del recurso si fue guardado exitosamente,
     * o `null` si no se guardó.
     * @private
     */
    async function FetchAndSaveData(url: string): Promise<Response | null> {
        const cacheStorage = await caches.open(CACHE_NAME);
        const params = GetParams(url);
        const endPoint = url.split("?")[0];

        if (params) {
            if (IMAGE_SIZES_TO_ACCEPT?.length > 0 && params.hasOwnProperty(IMAGE_SIZE_PARAM) && IMAGE_SIZES_TO_ACCEPT.indexOf(Number(params[IMAGE_SIZE_PARAM])) == -1) {
                console.debug(DEBUG_PREFIX, `File size <${params[IMAGE_SIZE_PARAM]}> cannot be saved! -> `, endPoint, params);
                const response = await fetch(url, { cache: 'no-store' });
                return response;
            }

            if (VARIANT_PARAM_FOR_CACHE_ITEM && params.hasOwnProperty(VARIANT_PARAM_FOR_CACHE_ITEM)) {
                const items = await cacheStorage.keys();

                for (const item of items) {
                    if (item.url.startsWith(endPoint)) {
                        let paramsFromUrlExisted = GetParams(item.url);
                        if (params[VARIANT_PARAM_FOR_CACHE_ITEM] != paramsFromUrlExisted[VARIANT_PARAM_FOR_CACHE_ITEM] && ObjectsHasSameParams(params, paramsFromUrlExisted)) {
                            let deleted = await cacheStorage.delete(item.url);
                            await cacheStorage.delete(item.url + META_SUFFIX);
                            console.debug(DEBUG_PREFIX, "Delete old resourse", deleted, endPoint, "OLD:", paramsFromUrlExisted); // , "NEW:", params);
                        }
                    }
                }
            }
        }

        // console.log(debugPrefix, 'Fetching fresh data', endPoint, params);

        const response = await fetch(url, { cache: 'no-store' });

        if (!response.ok) return null;

        // guardar en caché la respuesta
        await cacheStorage.put(url, response.clone());

        // guardar metadatos (timestamp de caché y expiración)
        const meta: IResponseCacheMeta = {
            cacheAt: Date.now(),
            expiresAt: Date.now() + EXPIRATION_TIME,
        }
        await cacheStorage.put(
            url + META_SUFFIX,
            new Response(JSON.stringify(meta), {
                headers: { 'Content-Type': 'application/json' }
            })
        );

        return response;
    }

    /**
     * @function GetCachedData
     * @description Obtiene datos de la caché para una URL específica.
     * @param {string} url - La URL del recurso que se desea obtener de la caché.
     * @returns {Promise<Response | null>} Promesa que resuelve en la respuesta del recurso si está en la caché,
     * o `null` si no se encuentra.
     * @private
     */
    async function GetCachedData(url): Promise<Response> {
        const cacheStorage = await caches.open(CACHE_NAME);
        const cachedResponse = await cacheStorage.match(url);

        if (!cachedResponse) {
            return null;
        }

        const metaResponse = await cacheStorage.match(url + META_SUFFIX);
        if (!metaResponse) return cachedResponse; // sin metadatos, asumir válido

        const meta: IResponseCacheMeta = await metaResponse.json();
        if (Date.now() > meta.expiresAt) {
            // caché expirada
            await cacheStorage.delete(url);
            await cacheStorage.delete(url + META_SUFFIX);
            console.debug(DEBUG_PREFIX, "Cache expired for", url);
            return null;
        }

        return cachedResponse;
    }

    export async function _CleanExpiredCache() {
        if (!isSupported()) return;

        const cacheStorage = await caches.open(CACHE_NAME);
        const requests = await cacheStorage.keys();
        const now = Date.now();

        for (const request of requests) {
            if (!request.url.endsWith(META_SUFFIX)) continue;

            const metaResponse = await cacheStorage.match(request);
            if (!metaResponse) continue;

            const meta: IResponseCacheMeta = await metaResponse.json();
            if (!meta?.expiresAt || meta.expiresAt > now) continue;
            const originalUrl = request.url.replace(META_SUFFIX, "");

            await _DeleteItemResource(originalUrl);
        }
    }

    /**
     * @function DeleteOldCaches
     * @description Elimina cachés antiguas que no coincidan con la versión actual, para respetar el espacio
     * en disco del usuario.
     * @returns {Promise<void>} Promesa que se resuelve cuando las cachés antiguas han sido eliminadas.
     * @private
     */
    async function DeleteOldCaches() {
        const keys = await caches.keys();

        for (const key of keys) {
            const isOurCache = key.startsWith(HOST_NAME);

            if (CACHE_NAME === key || !isOurCache) {
                continue;
            }

            caches.delete(key);
        }
    }

    /**
     * @function GetParams
     * @description Extrae los parámetros de una URL y los devuelve como un objeto clave-valor.
     * @param {string} url - La URL de la cual se desean extraer los parámetros.
     * @returns {Object | null} Objeto con los parámetros extraídos, o `null` si no hay parámetros.
     * @private
     */
    function GetParams(url: string): Record<string, string> | null {
        const u = new URL(url, location.origin);
        if (!u.search) return null;

        const obj: Record<string, string> = {};
        u.searchParams.forEach((v, k) => obj[k] = v);
        return obj;
    }


    /**
     * @function ObjectsHasSameParams
     * @description Compara dos objetos para verificar si tienen los mismos parámetros, excluyendo
     * ciertas claves específicas.
     * @param {Object} obj1 - El primer objeto a comparar.
     * @param {Object} obj2 - El segundo objeto a comparar.
     * @returns {boolean} `true` si los objetos tienen los mismos parámetros, `false` en caso contrario.
     * @private
     */
    function ObjectsHasSameParams(a: Object, b: Object): boolean {
        if (a === b) return true;
        if (!a || !b) return false;
        const ignore = new Set([VARIANT_PARAM_FOR_CACHE_ITEM, IMAGE_SIZE_PARAM]);

        const keysA = Object.keys(a).filter(k => !ignore.has(k));
        const keysB = Object.keys(b).filter(k => !ignore.has(k));

        if (keysA.length !== keysB.length) return false;

        return keysA.every(k => a[k] === b[k]);
    }

    /**
     * @function _GetXMLData
     * @description Obtiene datos en formato XML desde la caché o de forma remota si no están disponibles.
     * @param {string} url - La URL del recurso XML que se desea obtener.
     * @returns {Promise<Document | null>} Promesa que resuelve en un objeto `Document` si se obtiene el XML,
     * o `null` si no se encuentra.
     */
    export async function _GetXMLData(url: string): Promise<Document | null> {
        const cachedResponse = await GetCachedData(url);

        if (cachedResponse) {
            const text = await cachedResponse.text();
            return new DOMParser().parseFromString(text, "application/xml");
        }

        const fetched = await FetchAndSaveData(url);
        if (!fetched) {
            return null;
        }

        const freshResponse = await GetCachedData(url);
        if (freshResponse) {
            const text = await freshResponse.text();
            return new DOMParser().parseFromString(text, "application/xml");
        }

        return null;
    }
}


interface IResponseCacheMeta {
    cacheAt: number;
    expiresAt: number;
}